import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { LecturerRateDisplayType } from '../../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';
import { RequestRateStatus } from '../../../../../api/types/lecturers/price-rate/request-rate/request-rate-status';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import {
  defaultRatesReportFilter,
  RatesReportFilterType,
} from '../rates-report-filter.type';
import { SelectProfessorsFilterDialog } from './select-professors-filter-dialog';

interface RatesReportFilterProps {
  filters: RatesReportFilterType;
  loading?: boolean;
  onChange: (filters: RatesReportFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

const displayTypeOptions = [
  { label: 'שעה שלמה', value: LecturerRateDisplayType.OneHour },
  {
    label: 'שעה אקדמית (45 דק׳)',
    value: LecturerRateDisplayType.AcademicHour,
  },
];

const requestStatusOptions = [
  { label: 'ממתין', value: RequestRateStatus.Pending },
  { label: 'מאושר', value: RequestRateStatus.Approved },
  { label: 'נדחה', value: RequestRateStatus.Denied },
];

export const RatesReportFilter: React.FC<RatesReportFilterProps> = observer(
  ({ filters, loading, onChange, onSearch, onClear }) => {
    const [showProfessorsDialog, setShowProfessorsDialog] = useState(false);

    const update = <K extends keyof RatesReportFilterType>(
      key: K,
      value: RatesReportFilterType[K],
    ) => {
      onChange({ ...filters, [key]: value });
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
        return;
      }
      onChange(defaultRatesReportFilter());
    };

    const branchOptions = globalSettingsBranches.branches.map((b) => ({
      label: b.branchName,
      value: b.id,
    }));

    const professorsTooltip =
      filters.professors.length === 0
        ? 'לא נבחרו מרצים'
        : filters.professors
            .map((p) => `${p.firstName} ${p.lastName}`)
            .join(', ');

    const styles = {
      container: {
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      title: {
        fontSize: '1.1rem',
        fontWeight: 600 as const,
        color: '#333',
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: '12px',
      },
      field: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '4px',
      },
      label: {
        fontSize: '0.8rem',
        color: '#555',
      },
      rangeRow: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
      },
      inputFull: {
        width: '100%',
      },
      footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '12px',
        borderTop: '1px solid #eee',
        paddingTop: '12px',
      },
      checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      },
      checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      },
      buttonsRow: {
        display: 'flex',
        gap: '8px',
      },
      chipsRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '6px',
        gridColumn: '1 / -1',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>סינונים</div>
        </div>

        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>שלוחות</label>
            <MultiSelect
              value={filters.branchIds}
              options={branchOptions}
              onChange={(e) => update('branchIds', e.value ?? [])}
              placeholder="הכל"
              display="chip"
              filter
              maxSelectedLabels={2}
              selectedItemsLabel={`${filters.branchIds.length} שלוחות נבחרו`}
              style={styles.inputFull}
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>מרצים</label>
            <Button
              type="button"
              label={
                filters.professors.length === 0
                  ? 'הכל'
                  : String(filters.professors.length)
              }
              icon="pi pi-users"
              outlined
              severity="secondary"
              onClick={() => setShowProfessorsDialog(true)}
              disabled={loading}
              tooltip={professorsTooltip}
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              style={{
                ...styles.inputFull,
                justifyContent: 'space-between',
                direction: 'rtl',
              }}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>טווח תעריף</label>
            <div style={styles.rangeRow}>
              <InputNumber
                value={filters.rateMin}
                onValueChange={(e) => update('rateMin', e.value ?? null)}
                placeholder="מ-"
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                disabled={loading}
              />
              <InputNumber
                value={filters.rateMax}
                onValueChange={(e) => update('rateMax', e.value ?? null)}
                placeholder="עד"
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                disabled={loading}
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>טווח תעריף נסיעות</label>
            <div style={styles.rangeRow}>
              <InputNumber
                value={filters.travelRateMin}
                onValueChange={(e) => update('travelRateMin', e.value ?? null)}
                placeholder="מ-"
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                disabled={loading}
              />
              <InputNumber
                value={filters.travelRateMax}
                onValueChange={(e) => update('travelRateMax', e.value ?? null)}
                placeholder="עד"
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                style={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                disabled={loading}
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>סוג תצוגה</label>
            <MultiSelect
              value={filters.displayTypes}
              options={displayTypeOptions}
              onChange={(e) => update('displayTypes', e.value ?? [])}
              placeholder="הכל"
              display="chip"
              style={styles.inputFull}
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>סטטוס בקשה</label>
            <MultiSelect
              value={filters.requestStatuses}
              options={requestStatusOptions}
              onChange={(e) => update('requestStatuses', e.value ?? [])}
              placeholder="הכל"
              display="chip"
              style={styles.inputFull}
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>תוקף מ-</label>
            <Calendar
              value={filters.validityStart}
              onChange={(e) =>
                update('validityStart', (e.value as Date) ?? null)
              }
              dateFormat="dd/mm/yy"
              showIcon
              style={{ direction: 'ltr', ...styles.inputFull }}
              inputStyle={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>תוקף עד</label>
            <Calendar
              value={filters.validityEnd}
              onChange={(e) => update('validityEnd', (e.value as Date) ?? null)}
              dateFormat="dd/mm/yy"
              showIcon
              style={{ direction: 'ltr', ...styles.inputFull }}
              inputStyle={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          {filters.professors.length > 0 && (
            <div style={styles.chipsRow}>
              {filters.professors.map((professor) => (
                <Chip
                  key={professor.id}
                  label={`${professor.firstName} ${professor.lastName}`}
                  removable
                  onRemove={() =>
                    update(
                      'professors',
                      filters.professors.filter((p) => p.id !== professor.id),
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <div style={styles.checkboxRow}>
            <div style={styles.checkboxItem}>
              <Checkbox
                inputId="includeNoTravelRate"
                checked={filters.includeNoTravelRate}
                onChange={(e) =>
                  update('includeNoTravelRate', e.checked ?? false)
                }
                disabled={loading}
              />
              <label
                htmlFor="includeNoTravelRate"
                style={{ cursor: 'pointer' }}
              >
                כולל ללא תעריף נסיעות
              </label>
            </div>
          </div>

          <div style={styles.buttonsRow}>
            <Button
              icon="pi pi-filter-slash"
              label="נקה"
              text
              severity="secondary"
              onClick={handleClear}
              disabled={loading}
            />
            <Button
              icon="pi pi-search"
              label="חפש"
              onClick={onSearch}
              loading={loading}
            />
          </div>
        </div>

        <SelectProfessorsFilterDialog
          visible={showProfessorsDialog}
          onHide={() => setShowProfessorsDialog(false)}
          alreadySelectedProfessors={filters.professors}
          onApprove={(professors) => update('professors', professors)}
        />
      </div>
    );
  },
);
